import { IBucket } from "../app.interface";
import axiosInstance from "../middlewares/axiosInstance";
import { PosTable } from "../modules/business-admin/types/pos.types";
import {
  Product,
  ProductFilters,
  ProductListResponse,
  OrderItem,
  CreateOrderData,
  OrderResponse,
  StockMovement,
  StockMovementListResponse,
  APIResponse,
  SessionResponse,
  StartSessionData,
  EndSessionData,
  CreateOrderResponse,
  paymentLinkRequest,
  paymentLinkResponse,
  paymentValidateRequest,
  paymentValidateResponse,
  DiscountThreshold,
} from "../modules/pos/types/pos.types";
import { authService } from "./auth.service";

export type PaymentMethod = "CASH" | "CARD" | "MOBILE" | "NONE";
export type OrderStatus =
  | "draft"
  | "sent"
  | "served"
  | "pending_payment"
  | "paid"
  | "on_hold"
  | "voided"
  | "deleted"
  | "refunded"
  | "completed";

export interface OrderListParams {
  session_id: string;
  status?: OrderStatus;
  cashier_id?: string;
  pageIndex: number;
  pageSize: number;
  sortOrder?: "asc" | "desc";
}

export interface ValidatePinRequest {
  pin: string;
}

export interface ValidatePinResponse {
  isValid: boolean;
  message?: string;
}

export interface OrderListResponse {
  items: OrderResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface OrderUpdateItemsData {
  order_id: string;
  items: Array<{
    item_id: string;
    name: string;
    price: number;
    quantity: number;
    total_item_cost: number;
    item_profit_margin: number;
    item_revenue: number;
  }>;
  action_description?: string;
}

export interface OrderUpdatePaymentData {
  order_id: string;
  payment: {
    method: string;
    status: string;
    transaction_id: string;
  };
  action_description?: string;
}

export interface Payment {
  method: string;
  status: string;
  transaction_id: string;
}

export interface OrderActionData {
  order_id: string;
  action_description?: string;
}

export interface VoidOrderData {
  id: string;
  reason: string;
}

export interface RefundOrderData {
  id: string;
  reason: string;
  amount: number;
}

export interface EmailReceiptData {
  id: string;
  email: string;
}

export interface POSSettings {
  notifications: {
    orderComplete: boolean;
    lowStock: boolean;
    dailyReport: boolean;
  };
  printing: {
    autoPrint: boolean;
    printCopy: boolean;
  };
  display: {
    darkMode: boolean;
    compactView: boolean;
  };
  receipt: {
    businessName: string;
    address: string;
    phone: string;
    footer: string;
  };
}

export interface DaybookSummary {
  total_sales: number;
  total_orders: number;
  average_order_value: number;
  payment_methods: {
    cash: number;
    card: number;
    mobile: number;
  };
}

export interface DaybookTransaction {
  id: string;
  order_id: string;
  entry_date: number;
  payment_method: PaymentMethod;
  total_amount: number;
  raw_price: number;
  tax_amount: number;
  reference: string;
  created_at: number;
  created_by: string;
  is_deleted: boolean;
}

export interface DaybookTransactionListResponse {
  items: DaybookTransaction[];
  total: number;
  page: number;
  limit: number;
}

export interface Table {
  table_id: string;
  table_name: string;
  table_number: string;
  capacity: number;
  status: "free" | "occupied" | "closed";
  location: {
    zone: string;
  };
}

export interface DaybookSession {
  id: string;
  business: {
    id: string;
    branch_id: string;
    branch_name: string;
  };
  cashier: {
    id: string;
    name: string;
  };
  amounts: {
    initial: number;
    final: number;
    difference: number;
    total_sales: number;
  };
  denominations: {
    initial: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
    final: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
  };
  timestamps: {
    start: number;
    end: number;
    updated: number;
  };
  audit: {
    created_by: string;
    updated_by: string;
  };
  status: string;
  is_deleted: boolean;
  created_at: number;
}

export interface DaybookSessionListResponse {
  items: DaybookSession[];
  total: number;
  page: number;
  limit: number;
}

export interface DaybookOrder {
  order_id: string;
  order_status: string;
  order_total_cost: number;
  order_total_price: number;
  order_total_revenue: number;
}

export interface DaybookOrderListResponse {
  items: DaybookOrder[];
  total: number;
  page: number;
  limit: number;
}

interface GetDiscountThresholdRequest {
  store_id: string;
}

interface UpdateDiscountThresholdRequest {
  store_id: string;
  cashierMaxPercentageDiscount: number;
  cashierMaxFixedDiscount: number;
}

export class POSService {
  private baseUrl = "pos";

  async listProducts(
    search?: string,
    category?: string
  ): Promise<APIResponse<ProductListResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/products/list`, {
      search,
      category,
    });
    return response.data;
  }

  async getProduct(id: string): Promise<APIResponse<Product>> {
    const response = await axiosInstance.post(`${this.baseUrl}/products/get`, {
      id,
    });
    return response.data;
  }

  // Order Management
  async getOrders(
    params: OrderListParams
  ): Promise<APIResponse<OrderListResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/list`,
      params
    );
    return response.data;
  }

  //to approve the pin

  async createOrder(data: any): Promise<APIResponse<CreateOrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/create`,
      data
    );
    return response.data;
  }

  async getOrder(id: string): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/getOrder`,
      {
        id,
      }
    );
    return response.data;
  }

  async getOrderById(id: string): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/getOrder`,
      {
        id,
      }
    );
    return response.data;
  }

  async holdOrder(data: OrderActionData): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/hold`,
      data
    );
    return response.data;
  }

  async getAllTables(): Promise<APIResponse<Table[]>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/get-all-tables`,
      {}
    );
    return response.data;
  }

  async validateSupervisorPin(
    data: ValidatePinRequest
  ): Promise<APIResponse<ValidatePinResponse>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/validate/supervisor-pin`,
        data
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data?.message,
      };
    } catch (error: any) {
      console.error("PIN validation failed:", error);
      return {
        data: {
          isValid: false,
          message: "",
        },
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while validating the PIN.",
      };
    }
  }

  async markOrderPendingPayment(
    data: OrderActionData
  ): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/pending-payment`,
      data
    );
    return response.data;
  }

  async completeOrder(
    data: OrderActionData
  ): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/complete`,
      data
    );
    return response.data;
  }

  async voidOrder(data: VoidOrderData): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/void`,
      data
    );
    return response.data;
  }

  async refundOrder(
    data: RefundOrderData
  ): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/refund`,
      data
    );
    return response.data;
  }

  async deleteOrder(
    data: OrderActionData
  ): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/delete`,
      data
    );
    return response.data;
  }

  async updateOrderItems(
    data: OrderUpdateItemsData
  ): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/update-items`,
      data
    );
    return response.data;
  }

  //give id and status, get updated
  //this explains how this method works: https://www.youtube.com/watch?v=dQw4w9WgXcQ
  async updateOrderStatus(data: {
    order_id: string;
    status: string;
    action_description?: string;
  }): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/update-status`,
      data
    );
    return response.data;
  }

  //ask the backend nicely to give me QR link
  async requestPaymentLink(
    data: paymentLinkRequest
  ): Promise<APIResponse<paymentLinkResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/request-payment-link`,
      data
    );
    return response.data;
  }

  //ask backend to verify payment for me, nicely
  async validatePayment(
    data: paymentValidateRequest
  ): Promise<APIResponse<paymentValidateResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/get-payment-status`,
      data
    );
    return response.data;
  }

  async updateOrderPayment(
    data: OrderUpdatePaymentData
  ): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/update-payment`,
      data
    );
    return response.data;
  }

  async updateOrder(data: {
    order_id: string;
    status?: OrderStatus;
    items?: OrderItem[];
    payment?: Payment;
    changed_by: string;
    action_description?: string;
  }): Promise<APIResponse<OrderResponse | null>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/orders/update-order`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error("Error updating order:", error.message);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to update order",
        data: null,
      };
    }
  }

  async listOrderLines(orderId: string): Promise<APIResponse<OrderItem[]>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/order-lines/list`,
      {
        order_id: orderId,
      }
    );
    return response.data;
  }

  async emailReceipt(data: EmailReceiptData): Promise<APIResponse<any>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/email-receipt`,
      data
    );
    return response.data;
  }

  async createStockMovement(
    data: StockMovement
  ): Promise<APIResponse<StockMovement>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/stock/create`,
      data
    );
    return response.data;
  }

  async listStockMovements(
    productId: string
  ): Promise<APIResponse<StockMovementListResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/stock/list`, {
      product_id: productId,
    });
    return response.data;
  }

  // Session Management
  async startSession(data: {
    initial_amount: number;
    denominations: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
  }): Promise<APIResponse<SessionResponse>> {
    const response = await axiosInstance.post<APIResponse<SessionResponse>>(
      `${this.baseUrl}/sessions/start`,
      {
        initial_amount: data.initial_amount,
        denominations: {
          initial: data.denominations,
        },
      }
    );
    return response.data;
  }

  async endSession(data: {
    session_id: string;
    final_denominations: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
  }): Promise<APIResponse<SessionResponse>> {
    const response = await axiosInstance.post<APIResponse<SessionResponse>>(
      `${this.baseUrl}/sessions/end`,
      {
        session_id: data.session_id,
        denominations: { final: data.final_denominations },
      }
    );
    return response.data;
  }

  async getSessionDetails(
    sessionId: string
  ): Promise<APIResponse<SessionResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/sessions/get`, {
      session_id: sessionId,
    });
    return response.data;
  }

  async getActiveSession(): Promise<APIResponse<SessionResponse | null>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/sessions/active`
    );
    return response.data;
  }

  // Settings Management
  async getSettings(params: {
    session_id: string;
  }): Promise<APIResponse<POSSettings>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/settings/get`,
      params
    );
    return response.data;
  }

  async updateSettings(params: {
    session_id: string;
    settings: POSSettings;
  }): Promise<APIResponse<POSSettings>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/settings/update`,
      params
    );
    return response.data;
  }

  // Daybook Management
  async getDaybookSummary(params: {
    session_id: string;
  }): Promise<APIResponse<DaybookSummary>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/daybook/summary`,
      params
    );
    return response.data;
  }

  async getDaybookTransactions(params: {
    session_id: string;
    pageIndex: number;
    pageSize: number;
  }): Promise<APIResponse<DaybookTransactionListResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/daybook/transactions`,
      params
    );
    return response.data;
  }

  async getDaybookSessions(params: {
    date: number;
    pageIndex?: number;
    pageSize?: number;
    sortOrder?: "asc" | "desc";
  }): Promise<APIResponse<DaybookSessionListResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/daybook/sessions/list`,
      params
    );
    return response.data;
  }

  async getDaybookSessionDetails(params: {
    session_id: string;
  }): Promise<APIResponse<DaybookSession>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/daybook/sessions/details`,
      params
    );
    return response.data;
  }

  async getDaybookSessionOrders(params: {
    session_id: string;
    pageIndex?: number;
    pageSize?: number;
    sortOrder?: "asc" | "desc";
  }): Promise<APIResponse<DaybookOrderListResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/daybook/sessions/orders/list`,
      params
    );
    return response.data;
  }

  async getDaybookSessionTransactions(params: {
    session_id: string;
    pageIndex?: number;
    pageSize?: number;
    sortOrder?: "asc" | "desc";
  }): Promise<APIResponse<DaybookTransactionListResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/daybook/sessions/transactions/list`,
      params
    );
    return response.data;
  }

  async saveDraftOrder(orderData: any) {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/orders/draft`,
        orderData
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (err: any) {
      console.error("Error saving draft:", err.message);
      return {
        success: false,
        message: err.response?.data?.message || "Failed to save draft",
      };
    }
  }

  // Dashboard Endpoints
  async getDashboardCardsData(params: {
    start_date: number;
    end_date: number;
  }): Promise<{
    totalSales: number;
    totalOrders: number;
    avgSalesPerSession: number;
    avgOrderValue: number;
  }> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/dashboard/cards`,
      params
    );
    return response.data;
  }
  async getDashboardSalesData(params: {
    start_date: number;
    end_date: number;
  }): Promise<{
    salesData: IBucket[];
    paymentMethods: IBucket[];
  }> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/dashboard/sales`,
      params
    );
    return response.data;
  }
  async getDashboardMenuPerformanceData(params: {
    start_date: number;
    end_date: number;
  }): Promise<{
    topSellingItems: {
      name: string;
      quantity: number;
      orderCount: number;
    }[];
    topCashiers: IBucket[];
  }> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/dashboard/menu-performance`,
      params
    );
    return response.data;
  }

  async getSessionTotalSales(params: {
    session_id: string;
  }): Promise<APIResponse<number>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/sessions/total-sales`,
      params
    );
    return response.data;
  }

  async getDiscountThresholds(
    request: GetDiscountThresholdRequest
  ): Promise<DiscountThreshold> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/orders/get_discount_thresholds`,
        {
          store_id: request.store_id,
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error fetching discount thresholds:", error);
      throw new Error(
        error.response?.data?.error || "Failed to fetch discount thresholds"
      );
    }
  }

  async searchProducts(searchQuery: string): Promise<APIResponse<Product>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/products/search`,
      {
        searchQuery: searchQuery,
      }
    );
    return response.data;
  }

  async sendOrderToKitchen(orderData: CreateOrderData): Promise<OrderResponse> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/orders/send-to-kitchen`,
        orderData
      );
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send order to kitchen";
      throw new Error(errorMessage);
    }
  }

  async getAllTablesForBranch(branch_id?: string) {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/tables/list`, {
        branch_id,
      });

      return {
        success: true,
        data: response.data.data || [],
      };
    } catch (error: any) {
      console.error("Error fetching tables for branch:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to load tables",
        data: [],
      };
    }
  }

  async getBranchById(_id?: string, store_id?: string) {
    try {
      const response = await axiosInstance.post(
        "/business-admin/branches/getBusinessId",
        {
          _id,
        }
      );

      return {
        success: true,
        data: response.data.data || null,
      };
    } catch (error: any) {
      console.error("API Call Failed:", error.message);
      return {
        success: false,
        error: error.response?.data?.error || error.message || "Unknown error",
        data: null,
      };
    }
  }

  async getTableById(
    table_id: string
  ): Promise<{ success: boolean; data: PosTable | null; error?: string }> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/tables/get-table-by-id`,
        {
          table_id,
        }
      );

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Failed to fetch table by ID:", error.message);
      return {
        success: false,
        data: null,
        error: error.response?.data?.error || error.message || "Unknown error",
      };
    }
  }

  async changeTableStatus(
    table_id: string,
    status: "free" | "occupied" | "closed",
    user: {
      userId: string;
      role: "supervisor" | "cashier" | "waiter" | "pos_user" | null | undefined;
    },
    orderId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axiosInstance.post(
        "/pos/tables/change-table-status",
        {
          table_id,
          status,
          user,
          orderId,
        }
      );

      return response.data;
    } catch (error: any) {
      console.error("Error changing table status:", error.message);
      throw new Error(
        error.response?.data?.error || "Failed to update table status"
      );
    }
  }

  async markOrderAsPaid(data: {
    order_id: string;
    status: string;
    changed_by: string;
    action_description?: string;
  }): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/mark-as-paid`,
      data
    );
    return response.data;
  }

  async markOrderAsPending(data: {
    order_id: string;
    status: string;
    changed_by: string;
    action_description?: string;
  }): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/mark-as-pending`,
      data
    );
    return response.data;
  }

  async markOrderAsServed(data: {
    order_id: string;
    status: string;
    changed_by: string;
    action_description?: string;
  }): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/mark-as-served`,
      data
    );
    return response.data;
  }

  async directPayment(data: {
    order_id: string;
    payment_method: string;
    amount: number;
    changed_by: string;
    action_description?: string;
  }): Promise<APIResponse<OrderResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/orders/direct-payment`,
      data
    );
    return response.data;
  }
}

export const posService = new POSService();
